import { ref, Ref } from 'vue'
import { permissionsService } from '../services/permissionsService'
import { httpService } from '../services/httpService'
import { tokenService, tokensFromRefresh } from '../services/tokenService'
import { config } from '../services/configService'
import { eventBus } from '../eventBus'
import { log } from '../utils/DebugLogger'

export interface UserPermissions {
  user?: {
    id?: number
    name?: string
    email?: string
    [key: string]: any
  }
  [key: string]: any
}

export interface RegisterUserInput {
  username: string
  password: string
  email: string
  name: string
}

export interface AuthResult<T = void> {
  success: boolean
  data?: T
  error?: string
}

export type TokenStatus = 'EXPIRED' | 'EXPIRING_SOON' | 'FRESH' | 'INVALID'

// Shape returned by the external Cognito / Go loginApi services. Not a
// PHP-backed endpoint, so it cannot be wired via the contract pipeline —
// declared locally to give every httpService call site an explicit response
// type once httpService<T> defaults to `unknown` (Track D).
interface CognitoAuthResponse {
  success?: boolean
  error?: string
  accessToken?: string
  idToken?: string
  dados?: {
    accessToken?: string
    idToken?: string
    [key: string]: unknown
  }
  [key: string]: unknown
}

/**
 * O auth-service (Go) responde `{ Success, Code, Erro, Dados }`, com inicial
 * maiúscula; a tela lia só `success` / `error` / `dados` e tratava toda
 * resposta como falha — o código de recuperação era enviado e a tela dizia
 * que deu erro (chamado DS 2026-0077). Aceita as duas grafias num lugar só.
 */
export function authEnvelope(raw: unknown): CognitoAuthResponse {
  const r = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>
  const texto = (v: unknown): string | undefined => (typeof v === 'string' && v !== '' ? v : undefined)
  const dados = (r.dados ?? r.Dados) as CognitoAuthResponse['dados']

  return {
    ...r,
    success: r.success === true || r.Success === true,
    error: texto(r.error) ?? texto(r.Erro),
    dados: dados && typeof dados === 'object' ? dados : undefined,
  }
}

export function useAuth() {
  const isAuthenticated: Ref<boolean> = ref(false)
  const isLoading: Ref<boolean> = ref(true)
  const userPermissions: Ref<UserPermissions['user'] | null> = ref(null)

  async function checkAuth(): Promise<void> {
    try {
      log('useAuth', 'checkAuth', 'Checking authentication...')

      const permissions: UserPermissions = await (permissionsService as any).initialize({
        minutesToRefresh: 10,
        autoRefresh: true
      })

      if (permissions && permissions.user) {
        isAuthenticated.value = true
        userPermissions.value = permissions.user
        log('useAuth', 'checkAuth', '✅ User authenticated:', permissions.user)
      } else {
        log('useAuth', 'checkAuth', '❌ No user data in permissions')
        redirectToLogin()
      }
    } catch (error) {
      console.error('❌ Auth check failed:', error)
      redirectToLogin()
    } finally {
      isLoading.value = false
    }
  }

  function validateCurrentToken(): TokenStatus {
    if (!tokenService.hasTokens()) {
      log('useAuth', 'validateCurrentToken', '❌ No tokens in memory')
      return 'INVALID'
    }

    const status = tokenService.getTokenStatus() as TokenStatus
    log('useAuth', 'validateCurrentToken', `📊 Token status: ${status}`)

    return status
  }

  async function login(email: string, password: string): Promise<AuthResult> {
    try {
      log('useAuth', 'login', `🔐 Logging in user: ${email}`)

      // External Cognito auth service — no PHP contract applies
      const response = authEnvelope(await httpService.post<CognitoAuthResponse>(`${config.api.cognitoHost}/v1/user/logaUsuario`, {
        Username: email,
        Password: password
      }))

      log('useAuth', 'login', 'API response:', response)

      if (response.accessToken && response.idToken) {
        tokenService.setTokens(response.accessToken, response.idToken)
        tokenService.startRefreshScheduler(() => refreshToken().then(() => undefined))

        log('useAuth', 'login', '✅ Login successful, tokens stored')
        return { success: true }
      }

      if (response.success && response.dados?.accessToken && response.dados?.idToken) {
        tokenService.setTokens(response.dados.accessToken, response.dados.idToken)
        tokenService.startRefreshScheduler(() => refreshToken().then(() => undefined))

        log('useAuth', 'login', '✅ Login successful, tokens stored')
        return { success: true }
      }

      log('useAuth', 'login', '❌ Login failed:', response.error || 'Invalid credentials')
      return { success: false, error: response.error || 'Invalid credentials' }
    } catch (error: any) {
      log('useAuth', 'login', '❌ Login error:', error)
      return { success: false, error: error?.message || 'Login failed' }
    }
  }

  async function refreshToken(): Promise<AuthResult> {
    try {
      log('useAuth', 'refreshToken', '🔄 Refreshing tokens...')

      // External Cognito auth service — no PHP contract applies
      const response = await httpService.post<CognitoAuthResponse>(`${config.api.cognitoHost}/v1/user/refreshToken`)

      const tokens = tokensFromRefresh(response)
      if (tokens) {
        tokenService.setTokens(tokens.accessToken, tokens.idToken)
        log('useAuth', 'refreshToken', '✅ Tokens refreshed successfully')
        return { success: true }
      }

      log('useAuth', 'refreshToken', '❌ Refresh failed:', response.error)
      return { success: false, error: response.error || 'Refresh failed' }
    } catch (error: any) {
      log('useAuth', 'refreshToken', '❌ Refresh error:', error)
      return { success: false, error: error?.message || 'Refresh failed' }
    }
  }

  async function register(userData: RegisterUserInput): Promise<AuthResult<any>> {
    try {
      log('useAuth', 'register', `📝 Registering user: ${userData.email}`)

      // External Cognito auth service — no PHP contract applies
      const response = authEnvelope(await httpService.post<CognitoAuthResponse>(`${config.api.cognitoHost}/v1/user/registraUsuario`, {
        Username: userData.username,
        Password: userData.password,
        Email: userData.email,
        Name: userData.name
      }))

      if (response.success) {
        log('useAuth', 'register', '✅ Registration successful')
        return { success: true, data: response.dados }
      }

      log('useAuth', 'register', '❌ Registration failed:', response.error)
      return { success: false, error: response.error || 'Registration failed' }
    } catch (error: any) {
      log('useAuth', 'register', '❌ Registration error:', error)
      return { success: false, error: error?.message || 'Registration failed' }
    }
  }

  async function confirmAccount(username: string, code: string): Promise<AuthResult> {
    try {
      log('useAuth', 'confirmAccount', `✅ Confirming account: ${username}`)

      // External Cognito auth service — no PHP contract applies
      const response = authEnvelope(await httpService.post<CognitoAuthResponse>(`${config.api.cognitoHost}/v1/user/confirmaUsuario`, {
        Username: username,
        Code: code
      }))

      if (response.success) {
        log('useAuth', 'confirmAccount', '✅ Account confirmed')
        return { success: true }
      }

      log('useAuth', 'confirmAccount', '❌ Confirmation failed:', response.error)
      return { success: false, error: response.error || 'Confirmation failed' }
    } catch (error: any) {
      log('useAuth', 'confirmAccount', '❌ Confirmation error:', error)
      return { success: false, error: error?.message || 'Confirmation failed' }
    }
  }

  async function resendConfirmationCode(username: string): Promise<AuthResult> {
    try {
      log('useAuth', 'resendConfirmationCode', `Resending confirmation code for: ${username}`)

      // External Cognito auth service — no PHP contract applies
      const response = authEnvelope(await httpService.get<CognitoAuthResponse>(`${config.api.cognitoHost}/v1/user/reenviarConfirmacao/${username}`))

      if (response.success) {
        log('useAuth', 'resendConfirmationCode', 'Code resent')
        return { success: true }
      }

      log('useAuth', 'resendConfirmationCode', 'Resend failed:', response.error)
      return { success: false, error: response.error || 'Failed to resend confirmation code' }
    } catch (error: any) {
      log('useAuth', 'resendConfirmationCode', 'Resend error:', error)
      return { success: false, error: error?.message || 'Failed to resend confirmation code' }
    }
  }

  async function forgotPassword(username: string): Promise<AuthResult> {
    try {
      log('useAuth', 'forgotPassword', `🔑 Requesting password reset for: ${username}`)

      // External Cognito auth service — no PHP contract applies
      const response = authEnvelope(await httpService.get<CognitoAuthResponse>(`${config.api.cognitoHost}/v1/user/esqueciMinhaSenha/${username}`))

      if (response.success) {
        log('useAuth', 'forgotPassword', '✅ Password reset email sent')
        return { success: true }
      }

      log('useAuth', 'forgotPassword', '❌ Password reset failed:', response.error)
      return { success: false, error: response.error || 'Password reset request failed' }
    } catch (error: any) {
      log('useAuth', 'forgotPassword', '❌ Password reset error:', error)
      return { success: false, error: error?.message || 'Password reset request failed' }
    }
  }

  async function resetPassword(username: string, code: string, newPassword: string): Promise<AuthResult> {
    try {
      log('useAuth', 'resetPassword', `🔑 Resetting password for: ${username}`)

      // External Cognito auth service — no PHP contract applies
      const response = authEnvelope(await httpService.post<CognitoAuthResponse>(`${config.api.cognitoHost}/v1/user/confirmaEsqueciMinhaSenha`, {
        Username: username,
        Code: code,
        NewPassword: newPassword
      }))

      if (response.success) {
        log('useAuth', 'resetPassword', '✅ Password reset successful')
        return { success: true }
      }

      log('useAuth', 'resetPassword', '❌ Password reset failed:', response.error)
      return { success: false, error: response.error || 'Password reset failed' }
    } catch (error: any) {
      log('useAuth', 'resetPassword', '❌ Password reset error:', error)
      return { success: false, error: error?.message || 'Password reset failed' }
    }
  }

  async function logout(): Promise<void> {
    try {
      log('useAuth', 'logout', '🚪 Logging out user...')

      ;(tokenService as any).clearTokens()
      ;(permissionsService as any).clearAllData()

      eventBus.emit('user.logout')

      // External Cognito auth service — no PHP contract applies
      await httpService.get<CognitoAuthResponse>(`${config.api.cognitoHost}/v1/user/logout`)

      log('useAuth', 'logout', '✅ Logout successful - all data purged')

      redirectToLogin()
    } catch (error) {
      log('useAuth', 'logout', '❌ Logout error:', error)
      ;(tokenService as any).clearTokens()
      ;(permissionsService as any).clearAllData()
      redirectToLogin()
    }
  }

  async function globalLogout(): Promise<void> {
    try {
      log('useAuth', 'globalLogout', '🚪 Global logout...')

      ;(tokenService as any).clearTokens()
      ;(permissionsService as any).clearAllData()

      eventBus.emit('user.logout')

      // External Go loginApi — no PHP contract applies
      await httpService.get<CognitoAuthResponse>('/loginApi/v1/user/globalLogout')

      log('useAuth', 'globalLogout', '✅ Global logout successful - all data purged')

      redirectToLogin()
    } catch (error) {
      log('useAuth', 'globalLogout', '❌ Global logout error:', error)
      ;(tokenService as any).clearTokens()
      ;(permissionsService as any).clearAllData()
      redirectToLogin()
    }
  }

  function redirectToLogin(): void {
    const currentPath = window.location.pathname + window.location.search
    const loginUrl = (config as any).getLoginUrl(currentPath)
    window.location.href = loginUrl
  }

  return {
    isAuthenticated,
    isLoading,
    userPermissions,
    checkAuth,
    validateCurrentToken,
    login,
    refreshToken,
    logout,
    globalLogout,
    register,
    confirmAccount,
    resendConfirmationCode,
    forgotPassword,
    resetPassword
  }
}
